import { LoadingSpinner } from "@/components/ui/loading-spinner";
import type { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";

// 支持旧的自定义类型和新的 useInfiniteQuery 类型
interface LoadMoreQueryLike {
    data?: any[];
    hasMore?: boolean;
    isLoadingMore?: boolean;
    loadMore?: () => void;
}

interface LoadMoreButtonProps {
    query: LoadMoreQueryLike | UseInfiniteQueryResult<any, Error>;
}

export function LoadMoreButton( { query }: LoadMoreButtonProps ) {
    // 兼容两种类型的数据结构
    const isInfiniteQuery = "fetchNextPage" in query;

    let flatData: any[] = [];
    let hasMore = false;
    let isLoading = false;
    let handleLoadMore: () => void;

    if ( isInfiniteQuery ) {
        // React Query 的 useInfiniteQuery
        const infiniteQuery = query as UseInfiniteQueryResult<any, Error>;
        const infiniteData = infiniteQuery.data as InfiniteData<any, unknown> | undefined;
        flatData = infiniteData?.pages?.flatMap( ( page ) => {
            if ( Array.isArray( page ) ) {
                return page;
            }
            if ( page && Array.isArray( page.records ) ) {
                return page.records;
            }
            return [];
        } ) ?? [];
        hasMore = infiniteQuery.hasNextPage ?? false;
        isLoading = infiniteQuery.isFetchingNextPage ?? false;
        handleLoadMore = () => infiniteQuery.fetchNextPage();
    } else {
        // 自定义的 useInfinityQuery
        const customQuery = query as LoadMoreQueryLike;
        flatData = customQuery.data ?? [];
        hasMore = customQuery.hasMore ?? false;
        isLoading = customQuery.isLoadingMore ?? false;
        handleLoadMore = () => customQuery.loadMore?.();
    }

    // 没有数据或没有更多数据时不显示
    if ( flatData.length === 0 || !hasMore ) {
        return null;
    }

    return (
        <div className="mt-8 mb-8">
            <button
                onClick={handleLoadMore}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3 text-[15px] font-medium text-t3 hover:text-t1 active:bg-surface-subtle rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isLoading
                    ? (
                        <>
                            <LoadingSpinner size="sm" />
                            <span>Loading...</span>
                        </>
                    )
                    : (
                        "Load more"
                    )}
            </button>
        </div>
    );
}
